/**
 * Decks state atoms
 */
import { atom } from 'jotai';

import type { Deck, DeckCard, DeckFormat, DeckStatus } from '@/types/deck';

import { allDeckCardsAtom } from './allDeckCards';

// ============================================================================
// Decks List State
// ============================================================================

export interface DecksState {
  data: Deck[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Atom holding the shared decks list state - populated by init, updated by subscription */
export const decksStateAtom = atom<DecksState>({
  data: undefined,
  isLoading: true,
  error: null,
});

// ============================================================================
// Active Deck State (for Deck Editor)
// ============================================================================

/** Currently selected deck ID for editing */
export const activeDeckIdAtom = atom<string | null>(null);

export interface ActiveDeckState {
  deckId: string | null;
  deck: Deck | null;
  cards: DeckCard[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Derived atom for the currently active/editing deck.
 * Reads from global decksStateAtom and allDeckCardsAtom.
 */
export const activeDeckAtom = atom<ActiveDeckState>((get) => {
  const deckId = get(activeDeckIdAtom);
  const decksState = get(decksStateAtom);
  const allCardsState = get(allDeckCardsAtom);

  if (!deckId) {
    return {
      deckId: null,
      deck: null,
      cards: [],
      isLoading: false,
      error: null,
    };
  }

  const isLoading = decksState.isLoading || allCardsState.isLoading;
  const error = decksState.error || allCardsState.error;

  const deck = decksState.data?.find((d) => d.id === deckId) ?? null;
  const cards = allCardsState.data?.filter((c) => c.deck_id === deckId) ?? [];

  return {
    deckId,
    deck,
    cards,
    isLoading,
    error,
  };
});

// Legacy alias for backward compatibility during migration
export const activeDeckStateAtom = activeDeckAtom;

// ============================================================================
// Derived Atoms
// ============================================================================

/** Decks filtered by status */
export const decksByStatusAtom = atom((get) => {
  const { data } = get(decksStateAtom);
  if (!data) return { brewing: [], active: [], archived: [] };

  return data.reduce(
    (acc, deck) => {
      acc[deck.status].push(deck);
      return acc;
    },
    { brewing: [], active: [], archived: [] } as Record<DeckStatus, Deck[]>,
  );
});

/** Count of decks by status */
export const deckCountsAtom = atom((get) => {
  const byStatus = get(decksByStatusAtom);
  return {
    brewing: byStatus.brewing.length,
    active: byStatus.active.length,
    archived: byStatus.archived.length,
    total: byStatus.brewing.length + byStatus.active.length + byStatus.archived.length,
  };
});

/** All active deck IDs (for "in use" calculations) */
export const activeDeckIdsAtom = atom((get) => {
  const byStatus = get(decksByStatusAtom);
  return new Set(byStatus.active.map((d) => d.id));
});

// ============================================================================
// UI State
// ============================================================================

export type DeckListView = 'all' | 'brewing' | 'active' | 'archived';
export type DeckListSort = 'updated' | 'name' | 'format' | 'card_count';
export type DeckListSortDirection = 'asc' | 'desc';

export interface DeckListFilters {
  view: DeckListView;
  sort: DeckListSort;
  sortDirection: DeckListSortDirection;
  search: string;
  format: DeckFormat | 'all';
}

export const deckListFiltersAtom = atom<DeckListFilters>({
  view: 'all',
  sort: 'updated',
  sortDirection: 'desc',
  search: '',
  format: 'all',
});
